import React, { useEffect } from 'react';
import { DownloadIcon } from 'lucide-react';
import { cities } from '../data/cityData';
import { homeFaqs } from '../data/faqs';
import { businessEntity, websiteEntity, SERVICE_CATALOG, SERVICE_AREA_CITIES, SITE_URL, BUSINESS_ID, WEBSITE_ID } from '../utils/seo';
import { SITE_OVERVIEW, ROBOTS_TXT, SITEMAP_URLS, ROUTE_TABLE, PROMO_BAR_TEXT, NAVIGATION_ITEMS, FOOTER_COLUMNS, FOOTER_BOTTOM, HOME_PAGE, SERVICE_PAGES, SHARED_SERVICE_PAGE_BLOCKS, CITY_PAGE_TEMPLATE_SECTIONS, NOT_FOUND_PAGE, AEO_COVERAGE, ExportBlock, ExportPage } from '../data/siteContentExport';
import { setNoIndex } from '../utils/seo';
/**
 * CONTENT EXPORT — a documentation view of the entire website.
 *
 * This route exists purely to produce a readable, searchable PDF of the site's
 * content and SEO metadata. It is NOT linked from the site's navigation, is
 * marked noindex, and changes nothing about the live website.
 *
 * "Download PDF" opens the browser print dialog; choosing "Save as PDF"
 * produces a text-based (searchable, selectable) PDF rather than a screenshot.
 */
export function ContentExportPage() {
  useEffect(() => {
    document.title = "Website Content Export — Mike's Pro Detail";
    setNoIndex();
  }, []);
  const exportedOn = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <main className="export-doc min-h-screen w-full bg-white text-neutral-900" data-id="element-664" data-mp="2dt4qg8ta7lm8">
      <style data-id="element-665" data-mp="2iubzeb75uquh">{printStyles}</style>

      {/* Toolbar (screen only) */}
      <div className="no-print sticky top-0 z-10 border-b border-neutral-200 bg-white/95 backdrop-blur-sm" data-id="element-666" data-mp="2mv4a78vol3sv">
        <div className="mx-auto flex max-w-[900px] items-center justify-between gap-4 px-6 py-4" data-id="element-667" data-mp="2ro73dfwjqb8j">
          <div data-id="element-668" data-mp="2wkntugr7x1os">
            <p className="text-sm font-semibold text-neutral-900" data-id="element-669" data-mp="2wl7alu54tk18">
              Website Content Export
            </p>
            <p className="text-xs text-neutral-500" data-id="element-670" data-mp="31j8sibqtndb4">
              Documentation snapshot — the live site is unchanged
            </p>
          </div>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2" data-id="element-671" data-mp="2gx2hirg5szvv">
            <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-672" data-mp="2lt12s666y1s2" />
            Download PDF
          </button>
        </div>
      </div>

      <article className="mx-auto max-w-[900px] px-6 py-10 print:px-0 print:py-0" data-id="element-673" data-mp="2unxjdjs8qgpy">
        {/* ---------------- Cover ---------------- */}
        <header className="mb-10 border-b-2 border-neutral-900 pb-8" data-id="element-674" data-mp="39k6fwdkqyfyo">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-675" data-mp="34lbyng2fgzgo">
            Website Content &amp; SEO Export
          </p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight" data-id="element-676" data-mp="1f8rrq17dwol1">
            Mike's Pro Detail
          </h1>
          <p className="mt-2 text-lg text-neutral-600" data-id="element-677" data-mp="3lv65uuis9ynl">
            Complete content documentation of https://www.fulldetailpro.com
          </p>
          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2" data-id="element-678" data-mp="3qsplnyv4qjhn">
            <Field label="Exported" value={exportedOn} data-id="element-679" data-mp="3c431njuk53k3" />
            <Field label="Business" value={SITE_OVERVIEW.businessName} data-id="element-680" data-mp="3h47lnjuocgf2" />
            <Field label="Business type" value={SITE_OVERVIEW.businessType} data-id="element-681" data-mp="3h24ma97mtaxo" />
            <Field label="Phone" value={SITE_OVERVIEW.phone} data-id="element-682" data-mp="332v6hanc8iu8" />
            <Field label="Primary markets" value={SITE_OVERVIEW.primaryMarkets} data-id="element-683" data-mp="376jgdzzfzqin" />
            <Field label="Domain" value={SITE_OVERVIEW.domain} data-id="element-684" data-mp="2t68piwy4ubxh" />
            <Field label="Platform" value={SITE_OVERVIEW.framework} data-id="element-685" data-mp="2t839o91ky00i" />
            <Field label="Rendering" value={SITE_OVERVIEW.rendering} data-id="element-686" data-mp="2y6ehke8be0g3" />
            <Field label="Analytics" value={SITE_OVERVIEW.analytics} data-id="element-687" data-mp="rf5dc38lgpat" />
            <Field label="Form handler" value={SITE_OVERVIEW.formHandler} data-id="element-688" data-mp="rcsyw021ox2i" />
          </dl>
          <p className="mt-6 rounded-md border border-neutral-300 bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-700" data-id="element-689" data-mp="wd73170izmv9">
            This document is a text-based export of all publicly accessible
            content and SEO metadata on the website, organized by page and
            section. All wording is reproduced exactly as it appears on the live
            site. It is intended for analysis by search, AEO, and AI-search
            tooling.
          </p>
        </header>

        {/* ---------------- Table of contents ---------------- */}
        <Section title="Table of Contents" data-id="element-690" data-mp="24ragkjw19zsm">
          <ol className="ml-5 list-decimal space-y-1 text-sm text-neutral-700" data-id="element-691" data-mp="24pfwf7sl6bpl">
            <li data-id="element-692" data-mp="1zsz7ibdkbtbg">Site architecture &amp; indexing</li>
            <li data-id="element-693" data-mp="1vr49bfqxhw1w">Global navigation, promo bar &amp; footer</li>
            <li data-id="element-694" data-mp="1vorh89rgi5b4">Business entity &amp; structured data</li>
            <li data-id="element-695" data-mp="1qs15tzqljxw1">Homepage — full content</li>
            <li data-id="element-696" data-mp="1qtu9d65wy1zt">Homepage — FAQ content (19 questions)</li>
            <li data-id="element-697" data-mp="1ltps3dx6l38j">Service pages — full content ({SERVICE_PAGES.length} pages)</li>
            <li data-id="element-698" data-mp="1b5k412dnc2q">City / service-area pages ({cities.length} pages)</li>
            <li data-id="element-699" data-mp="1d0339u38u46">404 page</li>
            <li data-id="element-700" data-mp="697bd99yihpa">Image inventory &amp; alt text</li>
            <li data-id="element-701" data-mp="67w06oe6m7o5">AI-search question coverage map</li>
          </ol>
        </Section>

        {/* ---------------- 1. Architecture ---------------- */}
        <Section title="1. Site Architecture &amp; Indexing" data-id="element-702" data-mp="fax0u9ka36kf">
          <SubHeading data-id="element-703" data-mp="f82kat4dy5wa">Route table</SubHeading>
          <Table headers={['URL path', 'Page']} rows={ROUTE_TABLE.map(r => [r.path, r.page])} data-id="element-704" data-mp="3eqbnv1rhrwmw" />

          <SubHeading data-id="element-705" data-mp="3jntzizww8pyl">robots.txt</SubHeading>
          <Pre data-id="element-706" data-mp="3jmitsujw184u">{ROBOTS_TXT}</Pre>

          <SubHeading data-id="element-707" data-mp="3nqgq6xhtw4u7">sitemap.xml — {SITEMAP_URLS.length} canonical URLs</SubHeading>
          <ul className="ml-5 list-disc space-y-0.5 text-sm text-neutral-700" data-id="element-708" data-mp="3sgx7wtsomcmd">
            {SITEMAP_URLS.map(u => <li key={u} className="break-all" data-id="element-709" data-mp="2vz102th2bzz8">
                {u}
              </li>)}
          </ul>

          <SubHeading data-id="element-710" data-mp="300e36vsrtp2g">Indexing directives</SubHeading>
          <ul className="ml-5 list-disc space-y-1 text-sm text-neutral-700" data-id="element-711" data-mp="2zz30z0ez4pdg">
            <li data-id="element-712" data-mp="75472sxemmo5">
              All public pages emit:{' '}
              <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-713" data-mp="272m22gf4da1">
                index, follow, max-image-preview:large, max-snippet:-1
              </code>
            </li>
            <li data-id="element-714" data-mp="gxs15fx02cj6">
              The 404 route is the only page emitting{' '}
              <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-715" data-mp="bxnuehlw8w65">
                noindex, follow
              </code>
              . This export page is also noindex.
            </li>
            <li data-id="element-716" data-mp="3ff5prpkhcapo">
              Canonical URLs are normalized to HTTPS + www with no trailing
              slash (except the root).
            </li>
            <li data-id="element-717" data-mp="3pc0lk0pdbx09">
              Unknown city slugs under /ventura-county/ redirect to the homepage
              rather than rendering a thin page.
            </li>
          </ul>
        </Section>

        {/* ---------------- 2. Global UI ---------------- */}
        <Section title="2. Global Navigation, Promo Bar &amp; Footer" data-id="element-718" data-mp="14l9z5t6uy6ul">
          <SubHeading data-id="element-719" data-mp="1c414w4qdgp1p">Promotional bar (top of every page)</SubHeading>
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-720" data-mp="1h4oqgavu0doj">
            {PROMO_BAR_TEXT}
          </p>

          <SubHeading data-id="element-721" data-mp="127oa13j3c55d">Main navigation</SubHeading>
          <Table headers={['Label', 'Target']} rows={NAVIGATION_ITEMS.map(n => [n.label, n.target])} data-id="element-722" data-mp="128zdt3cjdcwh" />

          <SubHeading data-id="element-723" data-mp="17829vyajg8sb">Footer</SubHeading>
          {FOOTER_COLUMNS.map(col => <div key={col.heading} className="mb-4" data-id="element-724" data-mp="1uwv5ftn90eme">
              <p className="text-sm font-semibold text-neutral-900" data-id="element-725" data-mp="1zudj1xc7ni0q">
                {col.heading}
              </p>
              <ul className="ml-5 mt-1 list-disc space-y-0.5 text-sm text-neutral-700" data-id="element-726" data-mp="1l6ioscik15lj">
                {col.links.map(l => <li key={l.label} className="break-all" data-id="element-727" data-mp="1q3sabh2tox43">
                    {l.label} — {l.target}
                  </li>)}
              </ul>
            </div>)}
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-728" data-mp="2il4myjdfig7q">
            {FOOTER_BOTTOM}
          </p>
        </Section>

        {/* ---------------- 3. Structured data ---------------- */}
        <Section title="3. Business Entity &amp; Structured Data" data-id="element-729" data-mp="ikpvy4i7fvq8">
          <p className="text-sm leading-relaxed text-neutral-700" data-id="element-730" data-mp="dqltnyjvwbvh">
            Every page emits a single JSON-LD{' '}
            <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-731" data-mp="9oh1l4933nay">
              @graph
            </code>{' '}
            containing the canonical business entity and website entity, plus
            page-specific nodes. All nodes reference the business by a stable{' '}
            <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs" data-id="element-732" data-mp="xgntnr7s184k">
              @id
            </code>{' '}
            so search engines resolve the whole site to one business rather than
            competing duplicates.
          </p>

          <SubHeading data-id="element-733" data-mp="3d70k3f1ynesk">Entity identifiers</SubHeading>
          <Table headers={['Entity', '@id']} rows={[['LocalBusiness / AutoRepair', BUSINESS_ID], ['WebSite', WEBSITE_ID], ['Per-page WebPage / Service', `${SITE_URL}<path>#primary`], ['Per-page FAQPage', `${SITE_URL}<path>#faq`], ['Per-page BreadcrumbList', `${SITE_URL}<path>#breadcrumb`]]} data-id="element-734" data-mp="3d8v1wkhxqqsf" />

          <SubHeading data-id="element-735" data-mp="333v5oeugetcp">Canonical business entity (LocalBusiness + AutoRepair)</SubHeading>
          <Table headers={['Property', 'Value']} rows={[['name', businessEntity.name], ['telephone', businessEntity.telephone], ['url', businessEntity.url], ['priceRange', businessEntity.priceRange], ['address', 'CA, US (region only — mobile business, no storefront address)'], ['areaServed', `Ventura County, ${SERVICE_AREA_CITIES.join(', ')}`], ['description', businessEntity.description]]} data-id="element-736" data-mp="381odfcbenvf3" />
          <p className="mt-2 text-xs leading-relaxed text-neutral-500" data-id="element-737" data-mp="3qu920hk2ned3">
            No aggregateRating, review, opening-hours, geo-coordinate, or award
            data is emitted, because none of it is first-party verifiable from
            the site content.
          </p>

          <SubHeading data-id="element-738" data-mp="21dhq95ofuqxo">WebSite entity</SubHeading>
          <Table headers={['Property', 'Value']} rows={[['name', websiteEntity.name], ['url', websiteEntity.url], ['description', websiteEntity.description], ['publisher', 'References the business entity by @id'], ['inLanguage', websiteEntity.inLanguage]]} data-id="element-739" data-mp="21e9m8hly6qoh" />

          <SubHeading data-id="element-740" data-mp="244izl1lia8md">Service catalog (hasOfferCatalog)</SubHeading>
          <Table headers={['Service', 'URL', 'Description']} rows={SERVICE_CATALOG.map(s => [s.name, s.path, s.description])} data-id="element-741" data-mp="293vorszunrs4" />
        </Section>

        {/* ---------------- 4. Homepage ---------------- */}
        <Section title="4. Homepage — Full Content" data-id="element-742" data-mp="2j1tc8e9vxkjo">
          <PageMeta page={HOME_PAGE} data-id="element-743" data-mp="2j08gwmfqxxkr" />
          {HOME_PAGE.sections.map(s => <SectionBlock key={s.heading} heading={s.heading} headingLevel={s.headingLevel} blocks={s.blocks} data-id="element-744" data-mp="2ntujfls74l2u" />)}
        </Section>

        {/* ---------------- 5. Homepage FAQ ---------------- */}
        <Section title="5. Homepage FAQ — 19 Questions" data-id="element-745" data-mp="2wu98mw8a6kbm">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-746" data-mp="2wrn0ovhha2sv">
            These are rendered as a visible accordion on the homepage and
            emitted as FAQPage structured data from the same source, so schema
            and visible content match exactly.
          </p>
          <FaqList faqs={homeFaqs} data-id="element-747" data-mp="36zhhqtrvxn2j" />
        </Section>

        {/* ---------------- 6. Service pages ---------------- */}
        <Section title={`6. Service Pages — ${SERVICE_PAGES.length} Pages`} data-id="element-748" data-mp="2xwq5irr6qn9u">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-749" data-mp="2xyt5a3i53urr">
            Every service page also renders two shared blocks:{' '}
            <strong data-id="element-750" data-mp="3pne5i0h4hmlx">"{SHARED_SERVICE_PAGE_BLOCKS.serviceAreasHeading}"</strong>{' '}
            — {SHARED_SERVICE_PAGE_BLOCKS.serviceAreasBody} — and a closing CTA:{' '}
            <strong data-id="element-751" data-mp="3kutet7tijpmm">"{SHARED_SERVICE_PAGE_BLOCKS.ctaHeading}"</strong> —{' '}
            {SHARED_SERVICE_PAGE_BLOCKS.ctaBody} ({SHARED_SERVICE_PAGE_BLOCKS.ctaButtons}).
          </p>
          {SERVICE_PAGES.map(page => <div key={page.id} className="avoid-break mb-10 border-t border-neutral-200 pt-6" data-id="element-752" data-mp="chdx9cwzgaie">
              <h3 className="mb-3 text-xl font-bold tracking-tight" data-id="element-753" data-mp="26p818gpr44hl">
                6.{SERVICE_PAGES.indexOf(page) + 1} {page.name}
              </h3>
              <PageMeta page={page} data-id="element-754" data-mp="2bmzw9jlp06q0" />
              {page.sections.map(s => <SectionBlock key={s.heading} heading={s.heading} headingLevel={s.headingLevel} blocks={s.blocks} data-id="element-755" data-mp="1wzyap5ak2tvl" />)}
              {page.faqs && <>
                  <SubHeading data-id="element-756" data-mp="21woqdrig7ngh">FAQ (visible + FAQPage schema)</SubHeading>
                  <FaqList faqs={page.faqs} data-id="element-757" data-mp="1nxoymazn2sg3" />
                </>}
              {page.internalLinks && <>
                  <SubHeading data-id="element-758" data-mp="1syl24ng2wckz">Internal links</SubHeading>
                  <p className="text-sm text-neutral-700" data-id="element-759" data-mp="1e0so5zq6jw8w">
                    {page.internalLinks.join(' · ')}
                  </p>
                </>}
            </div>)}
        </Section>

        {/* ---------------- 7. City pages ---------------- */}
        <Section title={`7. City / Service-Area Pages — ${cities.length} Pages`} data-id="element-760" data-mp="38f3d086ryqeo">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-761" data-mp="3dcln41wj3bo9">
            Each city page follows the same structure. Shared sections across
            all city pages:
          </p>
          <ul className="mb-6 ml-5 list-disc space-y-1 text-sm text-neutral-700" data-id="element-762" data-mp="ws9nutykgkxb">
            {CITY_PAGE_TEMPLATE_SECTIONS.map(s => <li key={s} data-id="element-763" data-mp="1bhpca3amke9u">{s}</li>)}
          </ul>
          <p className="mb-6 text-sm leading-relaxed text-neutral-700" data-id="element-764" data-mp="16po4ouoejbq5">
            Structured data per city page: LocalBusiness/AutoRepair + WebSite +
            a <strong data-id="element-765" data-mp="1kf83xqirxqup">Service</strong> node ("Mobile Car Detailing in [City],
            CA") whose provider references the canonical business by @id, plus
            FAQPage and BreadcrumbList (Home / Service Areas / [City]). The city
            pages deliberately do <em data-id="element-766" data-mp="1pfdzhhs5zxi2">not</em> declare a physical business
            location in each city, because the business is fully mobile.
          </p>

          {cities.map((city, i) => <div key={city.slug} className="avoid-break mb-10 border-t border-neutral-200 pt-6" data-id="element-767" data-mp="2slooyslh0d" data-mpi={i}>
              <h3 className="mb-3 text-xl font-bold tracking-tight" data-id="element-768" data-mp="3r9lkxna0z629">
                7.{i + 1} {city.name}
              </h3>
              <dl className="mb-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-769" data-mp="1huzsovuvzzj">
                <Field label="URL" value={`${SITE_URL}/ventura-county/${city.slug}`} data-id="element-770" data-mp="1jxzk0mt97hg" />
                <Field label="SEO title" value={`${city.title} | Mike's Pro Detail`} data-id="element-771" data-mp="6f4v2l5xf5va" />
                <Field label="H1" value={city.title} data-id="element-772" data-mp="bfj5uaywwtx0" />
                <Field label="Meta description" value={city.metaDescription} data-id="element-773" data-mp="39yg966segv36" />
                <Field label="Canonical" value={`${SITE_URL}/ventura-county/${city.slug}`} data-id="element-774" data-mp="3ex14brssookr" />
                <Field label="Primary keyword" value={city.keyword} data-id="element-775" data-mp="3jw4715layipk" />
                <Field label="Robots" value="index, follow, max-image-preview:large, max-snippet:-1" data-id="element-776" data-mp="3jut395ruxayg" />
                <Field label="Open Graph" value="og:type=article, og:site_name, og:locale=en_US, og:url, og:title, og:description, og:image (logo); Twitter card = summary_large_image" data-id="element-777" data-mp="3oo5gykuzzxcf" />
              </dl>

              <SubHeading data-id="element-778" data-mp="2w4fa1bxjd62a">Subtitle</SubHeading>
              <p className="text-sm leading-relaxed text-neutral-700" data-id="element-779" data-mp="1dbox4in9byv5">
                {city.subtitle}
              </p>

              <SubHeading data-id="element-780" data-mp="19b4ytbqy6p7f">Body copy</SubHeading>
              {city.paragraphs.map((p, idx) => <p key={idx} className="mb-3 text-sm leading-relaxed text-neutral-700" data-id="element-781" data-mp="149xrfdspvs1a" data-mpi={idx}>
                  {p}
                </p>)}

              <SubHeading data-id="element-782" data-mp="zb4g9s0wkd4a">Neighborhoods listed</SubHeading>
              <p className="text-sm text-neutral-700" data-id="element-783" data-mp="ud2rqrkpjllf">
                {city.neighborhoods.join(' · ')}
              </p>

              <SubHeading data-id="element-784" data-mp="pl1b6tgib8pl">FAQ (visible + FAQPage schema)</SubHeading>
              <FaqList faqs={city.faqs} data-id="element-785" data-mp="ljpsz8zypprf" />
            </div>)}
        </Section>

        {/* ---------------- 8. 404 ---------------- */}
        <Section title="8. 404 Page" data-id="element-786" data-mp="gmft0zey5vpc">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-787" data-mp="gjbk5oqjebsc">
            <Field label="URL" value={NOT_FOUND_PAGE.url} data-id="element-788" data-mp="bljuz2ha13ru" />
            <Field label="SEO title" value={NOT_FOUND_PAGE.seoTitle} data-id="element-789" data-mp="6mgpjgxdwvjc" />
            <Field label="Robots" value={NOT_FOUND_PAGE.robots} data-id="element-790" data-mp="3t4qp8pgm33h2" />
            <Field label="H1" value={NOT_FOUND_PAGE.h1} data-id="element-791" data-mp="3t35x16hrrwmd" />
            <Field label="Body" value={NOT_FOUND_PAGE.body} data-id="element-792" data-mp="1yz3s3ds4f0l" />
            <Field label="Calls to action" value={NOT_FOUND_PAGE.ctas} data-id="element-793" data-mp="1wcxe72mk5jy" />
          </dl>
        </Section>

        {/* ---------------- 9. Images ---------------- */}
        <Section title="9. Image Inventory &amp; Alt Text" data-id="element-794" data-mp="3o6fhck9vn31h">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-795" data-mp="fq47pho6jtgq">
            All images live on the homepage; service and city pages are
            text-only apart from the shared header. Every image has descriptive
            alt text.
          </p>
          <Table headers={['File', 'Alt text', 'Notes']} rows={(HOME_PAGE.images ?? []).map(img => [img.src, img.alt, img.notes ?? '—'])} data-id="element-796" data-mp="6xaeegbwetjv" />
        </Section>

        {/* ---------------- 10. AEO ---------------- */}
        <Section title="10. AI-Search Question Coverage Map" data-id="element-797" data-mp="ymvj74xhd4a8">
          <p className="mb-4 text-sm leading-relaxed text-neutral-700" data-id="element-798" data-mp="13g869amih30v">
            Where the site answers the questions prospective customers ask
            search engines and AI assistants.
          </p>
          <Table headers={['Question', 'Answered on']} rows={AEO_COVERAGE.map(a => [a.question, a.answeredOn])} data-id="element-799" data-mp="2j67pw1h6h9aj" />
        </Section>

        <footer className="mt-12 border-t border-neutral-300 pt-6 text-xs text-neutral-500" data-id="element-800" data-mp="2swk9nrh64yfm">
          <p data-id="element-801" data-mp="2sywq207a30qk">
            End of export — Mike's Pro Detail, https://www.fulldetailpro.com —
            generated {exportedOn}. Content reproduced verbatim from the live
            site.
          </p>
        </footer>
      </article>
    </main>;
}
/* ------------------------------------------------------------------ */
/* Presentational helpers                                              */
/* ------------------------------------------------------------------ */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mb-12" data-id="element-802" data-mp="3lkpusfc4wpfz">
      <h2 className="mb-4 border-b border-neutral-300 pb-2 text-2xl font-bold tracking-tight" dangerouslySetInnerHTML={{
      __html: title
    }} data-id="element-803" data-mp="3lnkcw07ody68" />
      {children}
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h4 className="mb-2 mt-5 text-xs font-bold uppercase tracking-[0.14em] text-neutral-500" data-id="element-804" data-mp="1svypz2lj3sq3">
      {children}
    </h4>;
}
function Field({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex flex-col" data-id="element-805" data-mp="22m0ajkeoat09">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500" data-id="element-806" data-mp="26o53egxarllu">
        {label}
      </dt>
      <dd className="break-words text-sm text-neutral-800" data-id="element-807" data-mp="2bmq56ks76dce">{value}</dd>
    </div>;
}
function Pre({
  children
}: {
  children: React.ReactNode;
}) {
  return <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-neutral-300 bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-800" data-id="element-808" data-mp="2gljh48ru5wag">
      {children}
    </pre>;
}
function Table({
  headers,
  rows
}: {
  headers: string[];
  rows: string[][];
}) {
  return <table className="w-full table-auto border-collapse text-sm" data-id="element-809" data-mp="epu3fka3gtj6">
      <thead data-id="element-810" data-mp="9yc4f1rmt30u">
        <tr data-id="element-811" data-mp="9xslbhq8w8l8">
          {headers.map(h => <th key={h} className="border-b border-neutral-300 py-2 pr-4 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-500" data-id="element-812" data-mp="9whlfszx7kze">
              {h}
            </th>)}
        </tr>
      </thead>
      <tbody data-id="element-813" data-mp="1pl4heyek0x">
        {rows.map((row, i) => <tr key={i} className="avoid-break align-top" data-id="element-814" data-mp="xnd522u839vk" data-mpi={i}>
            {row.map((cell, j) => <td key={j} className="border-b border-neutral-200 py-2 pr-4 leading-relaxed text-neutral-800" data-id="element-815" data-mp="xlahrqkgfuuj" data-mpi={j}>
                {cell}
              </td>)}
          </tr>)}
      </tbody>
    </table>;
}
function FaqList({
  faqs
}: {
  faqs: {
    question: string;
    answer: string;
  }[];
}) {
  return <dl className="space-y-3" data-id="element-816" data-mp="isguhiyxyafb">
      {faqs.map(f => <div key={f.question} className="avoid-break" data-id="element-817" data-mp="38fy5x3b8i6ij">
          <dt className="text-sm font-semibold text-neutral-900" data-id="element-818" data-mp="38dbq6ge6wkp8">
            Q: {f.question}
          </dt>
          <dd className="mt-0.5 text-sm leading-relaxed text-neutral-700" data-id="element-819" data-mp="2394axe926dtx">
            A: {f.answer}
          </dd>
        </div>)}
    </dl>;
}
function PageMeta({
  page
}: {
  page: ExportPage;
}) {
  return <div className="avoid-break mb-5 rounded border border-neutral-300 bg-neutral-50 p-4" data-id="element-820" data-mp="1tjb04fgqq2qq">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm" data-id="element-821" data-mp="1ok841kiqn6uw">
        <Field label="URL" value={page.url} data-id="element-822" data-mp="1olszrdgsgvuc" />
        <Field label="SEO title" value={page.seoTitle} data-id="element-823" data-mp="2m7qceu8hm6yd" />
        <Field label="Meta description" value={page.metaDescription} data-id="element-824" data-mp="2m77507ggr0yl" />
        <Field label="Canonical" value={page.canonical} data-id="element-825" data-mp="2h9x68176pcy3" />
        <Field label="Robots" value={page.robots} data-id="element-826" data-mp="2c9soy8ygce6t" />
        <Field label="H1" value={page.h1} data-id="element-827" data-mp="2c8hjm4pcyydl" />
        <Field label="Structured data" value={page.schema} data-id="element-828" data-mp="2887fyyp1gk1w" />
        <Field label="Open Graph / Twitter" value={page.openGraph.map(o => `${o.label}: ${o.value}`).join(' | ')} data-id="element-829" data-mp="287nw3cftvll8" />
      </dl>
    </div>;
}
function SectionBlock({
  heading,
  headingLevel,
  blocks
}: {
  heading: string;
  headingLevel?: string;
  blocks: ExportBlock[];
}) {
  return <div className="avoid-break mb-6" data-id="element-830" data-mp="38hf6801l59kj">
      <h4 className="mb-2 text-base font-bold text-neutral-900" data-id="element-831" data-mp="38eaxqqh37ro2">
        {heading}
        {headingLevel && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400" data-id="element-832" data-mp="3cfwcyih3gi1f">
            {headingLevel}
          </span>}
      </h4>
      {blocks.map((block, i) => {
      if (block.type === 'p') {
        return <p key={i} className="mb-2 text-sm leading-relaxed text-neutral-700" data-id="element-833" data-mp="3r759lbjez3o2">
              {block.text}
            </p>;
      }
      if (block.type === 'h3') {
        return <p key={i} className="mb-1 mt-3 text-sm font-semibold text-neutral-900" data-id="element-834" data-mp="2f14zh5rbry">
              {block.text}
            </p>;
      }
      if (block.type === 'list') {
        return <ul key={i} className="mb-2 ml-5 list-disc space-y-1 text-sm leading-relaxed text-neutral-700" data-id="element-835" data-mp="90qqky6ag3it">
              {block.items.map(item => <li key={item} data-id="element-836" data-mp="2asnaav2m0joo">{item}</li>)}
            </ul>;
      }
      if (block.type === 'code') {
        return <Pre key={i} data-id="element-837" data-mp="3e3jb76avxxtm">{block.text}</Pre>;
      }
      return <dl key={i} className="mb-2 space-y-1 text-sm" data-id="element-838" data-mp="3e42ryjosug62" data-mpi={i}>
            {block.items.map(item => <div key={item.label} className="flex flex-wrap gap-x-2" data-id="element-839" data-mp="395hq6ftwfofi">
                <dt className="font-semibold text-neutral-900" data-id="element-840" data-mp="3n4quoi8qsu3n">
                  {item.label}:
                </dt>
                <dd className="text-neutral-700" data-id="element-841" data-mp="3i4uz97e42azp">{item.value}</dd>
              </div>)}
          </dl>;
    })}
    </div>;
}
const printStyles = `
  @media print {
    .no-print { display: none !important; }
    .export-doc { background: #fff !important; color: #000 !important; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    h2 { break-after: avoid; page-break-after: avoid; }
    a { text-decoration: none; color: inherit; }
  }
  @page {
    margin: 16mm 14mm;
  }
`;